import React from "react";
import { Box, Container, Typography, Stack, Button } from "@mui/material";
import { motion } from "framer-motion";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

const MONO   = "'JetBrains Mono', 'Fira Code', ui-monospace, monospace";
const ACCENT = "#E2684A";
const ACCENT2 = "#F3C969";
const PURPLE = "#7C3AED";
const BG     = "#030014";
const LINE   = "#1E2130";
const INK    = "#E7E9EE";
const INK2   = "#B6BBC6";
const INK3   = "#7D8390";
const GOOD   = "#8BD17C";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 22 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.55, delay },
});

const Hero = () => (
  <Box sx={{
    pt: { xs: 14, md: 17 }, pb: { xs: 9, md: 12 },
    borderBottom: `1px solid ${LINE}`,
    position: "relative", overflow: "hidden", background: BG,
  }}>
    {/* Static radial glows */}
    <Box sx={{
      position: "absolute", inset: 0, pointerEvents: "none",
      background: `
        radial-gradient(800px 400px at 75% -5%, rgba(226,104,74,0.09), transparent 60%),
        radial-gradient(600px 400px at 5% 100%, rgba(124,58,237,0.07), transparent 60%)
      `,
    }} />

    <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
      <motion.div {...fadeUp(0)}>
        <Typography sx={{
          fontFamily: MONO, fontSize: "12px", letterSpacing: "0.16em",
          textTransform: "uppercase", color: ACCENT, mb: 2,
        }}>
          Strategic AI Partner · Enterprise &amp; GCC
        </Typography>
      </motion.div>

      <motion.div {...fadeUp(0.08)}>
        <Typography sx={{
          fontFamily: "'IBM Plex Serif', serif",
          fontSize: { xs: "2.1rem", md: "3.2rem", lg: "3.8rem" },
          fontWeight: 600, letterSpacing: "-0.02em", lineHeight: 1.05,
          color: INK, mb: 3, maxWidth: 860,
        }}>
          Build AI That{" "}
          <Box component="span" sx={{
            background: `linear-gradient(120deg, ${ACCENT}, ${ACCENT2})`,
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
          }}>
            Actually Ships
          </Box>
        </Typography>
      </motion.div>

      <motion.div {...fadeUp(0.14)}>
        <Typography sx={{
          color: INK2, lineHeight: 1.7, maxWidth: 660,
          fontSize: { xs: "1.05rem", md: "1.15rem" }, mb: 4,
        }}>
          Go from concept to production in weeks, not years.
          We design, build, and deploy custom AI solutions — voice agents, LLMs, and automation —
          that deliver measurable ROI from day one.
        </Typography>
      </motion.div>

      <motion.div {...fadeUp(0.2)}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} mb={4}>
          <Button
            variant="contained"
            size="large"
            endIcon={<ArrowForwardIcon />}
            onClick={() => document.getElementById("solutions")?.scrollIntoView({ behavior: "smooth" })}
            sx={{ px: 4, py: 1.6, fontSize: "14px" }}
          >
            Explore Services
          </Button>
          <Button
            variant="outlined"
            size="large"
            data-cal-link="nirman-agrawal/30-min-meeting"
            data-cal-namespace="30-min-meeting"
            data-cal-config='{"layout":"month_view","useSlotsViewOnSmallScreen":"true"}'
            sx={{ px: 4, py: 1.6, fontSize: "14px", cursor: "pointer" }}
          >
            Book Strategy Call
          </Button>
        </Stack>
      </motion.div>

      <motion.div {...fadeUp(0.26)}>
        <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1.5}
          sx={{ pt: 2, borderTop: `1px dashed ${LINE}` }}>
          {["SOC 2 Ready", "<500ms Latency", "On-Premise Deployable"].map(p => (
            <Box key={p} sx={{
              border: `1px solid ${LINE}`, px: 1.4, py: 0.5,
              borderRadius: "9999px", fontFamily: MONO, fontSize: "12px", color: INK2,
            }}>{p}</Box>
          ))}
          <Typography sx={{ fontFamily: MONO, fontSize: "12px", color: INK3 }}>
            Production-grade AI for enterprise engineering teams.
          </Typography>
        </Stack>
      </motion.div>

      {/* Terminal code atmosphere */}
      <motion.div {...fadeUp(0.32)}>
        <Box sx={{
          mt: 5, background: "#0d0e12", border: `1px solid ${LINE}`,
          borderRadius: "14px", p: "18px 22px", fontFamily: MONO, fontSize: "13px",
          color: INK2, overflowX: "auto", maxWidth: 620,
        }}>
          <Box component="span" sx={{ color: INK3 }}># typical engagement, sprint 1</Box><br />
          <Box component="span" sx={{ color: ACCENT2 }}>beanbag</Box>
          {" /assess "}
          <Box component="span" sx={{ color: GOOD }}>your-org/ai-readiness</Box><br />
          <Box component="span" sx={{ color: ACCENT2 }}>beanbag</Box>
          {" /build "}
          <Box component="span" sx={{ color: ACCENT }}>voice-agent</Box>
          {" --env "}
          <Box component="span" sx={{ color: GOOD }}>production</Box><br />
          <Box component="span" sx={{ color: INK3 }}>{"# > deployed in 3 weeks. 70% support volume automated."}</Box>
        </Box>
      </motion.div>
    </Container>
  </Box>
);

export default Hero;
